import { LockClosedIcon } from '@heroicons/react/solid'
import { useAppContext } from 'context/AppContext'
import { useAuth } from 'context/AuthContext'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { CognitoErrorTypes } from 'types/cognito'
import { classNames } from 'utils/dom-helpers'
import { SubmitError } from '../types'
import Translation from 'components/Translation'
import changeTemporaryPassword from 'services/cognito/respond-to-auth-challenge'
import InputSpinner from 'components/Spinners/input'
import { FormValues, StyledMaskedTextBox, VIEW_MODES } from './types'
import resendVerification from 'services/cognito/resend-verification'
import { useAppErrorApiContext } from 'context/AppErrorContext'
import { EMAIL_REGEX } from 'types/common'
import InputValidations from 'components/Validations/Field'
import { LoginModalCloser } from '.'
import getConfig from 'next/config'

type TenantLoginProps = {
    hide_close_button?: boolean
}

export default function TenantLogin(props: TenantLoginProps) {
    const { hide_close_button } = props
    const router = useRouter()
    const ctx = useAuth()
    const { setGlobalError } = useAppErrorApiContext()
    const { translations, loadComponentTranslations, setGlobalLoading } =
        useAppContext()
    const [loading, toggleLoading] = useState(false)
    const [mode, setMode] = useState<VIEW_MODES>(VIEW_MODES.LOGIN)
    const [resend_email, setResendEmail] = useState('')
    const [Session, setSession] = useState<string>()
    const { NEXT_PUBLIC_STATIC_DOMAIN, LANDING_PAGES_HOST } = getConfig().publicRuntimeConfig

    const {
        register,
        handleSubmit,
        formState: { errors },
        setError,
        reset,
    } = useForm<FormValues>({
        mode: 'onSubmit',
    })

    useEffect(() => {
        if (!translations.LOGIN_TRANSLATION_ID) {
            loadComponentTranslations('LOGIN_TRANSLATION_ID')
        }
    }, [translations])

    const onSubmitRequestReset: SubmitHandler<FormValues> = async (
        values: Record<string, string>
    ) => {
        toggleLoading(true)
        try {
            const res = await ctx.forgotPassword(values.email)
            setMode(VIEW_MODES.NEW_PASSWORD)
        } catch (e) {
            const { name, message } = e as SubmitError
            setError('email', {
                type: name,
                message,
            })
        }
        toggleLoading(false)
    }

    const onSubmitNewPassword: SubmitHandler<FormValues> = async (
        values: Record<string, string>
    ) => {
        toggleLoading(true)
        try {
            await ctx.confirmForgotPassword(
                values.email,
                values.new_password,
                values.code
            )
            reset()
            setMode(VIEW_MODES.LOGIN)
        } catch (e) {
            const { name, message } = e as SubmitError
            if (name == CognitoErrorTypes.InvalidParameterException) {
                setError('new_password', {
                    type: CognitoErrorTypes.InvalidParameterException,
                    message,
                })
            } else if (name == CognitoErrorTypes.InvalidPasswordException) {
                setError('new_password', {
                    type: CognitoErrorTypes.InvalidPasswordException,
                    message,
                })
            } else {
                setError('email', { message })
            }
        }
        toggleLoading(false)
    }

    const onSubmitNewPasswordWithChallenge: SubmitHandler<
        FormValues
    > = async (values: Record<string, string>) => {
        toggleLoading(true)
        try {
            await changeTemporaryPassword(values, Session)
            reset()
            setMode(VIEW_MODES.LOGIN)
        } catch (e) {
            const { name, message } = e as SubmitError
            if (name == CognitoErrorTypes.InvalidParameterException) {
                setError('new_password', {
                    type: CognitoErrorTypes.InvalidParameterException,
                    message,
                })
            } else if (name == CognitoErrorTypes.InvalidPasswordException) {
                setError('new_password', {
                    type: CognitoErrorTypes.InvalidPasswordException,
                    message,
                })
            } else {
                setError('email', { message })
            }
        }
        toggleLoading(false)
    }

    const onSubmit: SubmitHandler<FormValues> = async (
        values: Record<string, string>
    ) => {
        try {
            toggleLoading(true)
            const { email, password } = values
            const res = await ctx.login(email, password)

            const { AuthenticationResult, ChallengeName, Session } = res
            if (AuthenticationResult) {
                reset()
            } else if (ChallengeName && Session) {
                setSession(Session)
                setMode(VIEW_MODES.REQUIRE_NEW_PASSWORD)
            }
        } catch (e) {
            const { name, message } = e as SubmitError
            if (name == CognitoErrorTypes.NotAuthorizedException) {
                setError('email', {
                    type: CognitoErrorTypes.NotAuthorizedException,
                    message,
                })
                setError('password', {
                    type: CognitoErrorTypes.NotAuthorizedException,
                    message,
                })
            } else if (
                name == CognitoErrorTypes.UserNotConfirmedException
            ) {
                setError('email', {
                    type: CognitoErrorTypes.UserNotConfirmedException,
                    message,
                })
                setError('password', {
                    type: CognitoErrorTypes.UserNotConfirmedException,
                    message,
                })
                setResendEmail(values.email)
            }
        } finally {
            toggleLoading(false)
        }
    }

    const onResendEmail = async () => {
        try {
            setGlobalLoading(true)

            await resendVerification({ email: resend_email })

            ctx.EmailVerificationModal.setAttributes({
                email: resend_email,
            })
            ctx.EmailVerificationModal.open()
        } catch (err) {
            setGlobalError(err.error)
        } finally {
            setGlobalLoading(false)
            setResendEmail('')
        }
    }

    const primaryColorStyle = { color: '#23476B' } as React.CSSProperties
    const labelStyles = `text-lg font-bold 2xl:text-2xl`
    return (
        <>
            <div className="md:w-1/2 sm:w-full loginfields">
                <div className="flex flex-col  h-full">
                    <div className="flex-shrink-0 flex items-center login-logo">
                        <div className="block sm:block lg:block">
                            <a
                                href={LANDING_PAGES_HOST}
                                className="bg-contain app-logo-icon bg-left block cursor-pointer"
                            />
                        </div>
                    </div>
                    <div className="flex flex-col">
                        <div className="flex justify-between text-gray-500  w-full">
                            {mode == VIEW_MODES.LOGIN ? (
                                <Translation
                                    content_key="login_title"
                                    render_as="span"
                                    className="logintitle"
                                />
                            ) : (
                                <button
                                    className="text-primary"
                                    onClick={() => {
                                        setMode(VIEW_MODES.LOGIN)
                                    }}
                                >
                                    <span>&larr; </span>
                                    <Translation
                                        content_key="back_to_login"
                                        render_as="span"
                                        className="underline"
                                    />
                                </button>
                            )}
                        </div>
                        <form
                            method="POST"
                            onSubmit={handleSubmit(onSubmit)}
                            className="z-20 pt-6 2xl:pt-6"
                        >
                            <div className="loginform">
                                <fieldset className=" form-group">
                                    <label
                                        htmlFor="email-address"
                                        className={classNames(
                                            'block ',
                                            errors.email?.type
                                                ? 'text-red-700'
                                                : ''
                                        )}
                                    >
                                        <Translation content_key="email" />
                                        <sup>*</sup>
                                    </label>
                                    <input
                                        type="text"
                                        id="email-address"
                                        autoComplete="email"
                                        readOnly={
                                            mode == VIEW_MODES.NEW_PASSWORD
                                        }
                                        placeholder={
                                            translations['enter_email']
                                        }
                                        className={classNames(
                                            ' focus:ring-primary-light focus:border-primary-light block w-full ',
                                            errors.email?.type
                                                ? 'border-red-300 bg-red-100'
                                                : '',
                                            'h-12'
                                        )}
                                        {...register('email', {
                                            required: true,
                                            pattern: EMAIL_REGEX,
                                        })}
                                    />
                                    <InputValidations
                                        field_error={errors.email}
                                        required_key="common.login_email_required"
                                        pattern_key="common.login_valid_email_required"
                                    />
                                </fieldset>

                                {mode == VIEW_MODES.LOGIN ? (
                                    <>
                                        <fieldset className="form-group mb-0">
                                            <label
                                                htmlFor="password"
                                                className={classNames(
                                                    'block ',
                                                    errors.password?.type
                                                        ? 'text-red-700'
                                                        : ''
                                                )}
                                            >
                                                <Translation content_key="password" />
                                                <sup>*</sup>
                                            </label>
                                            <input
                                                type="password"
                                                id="password"
                                                autoComplete="current-password"
                                                placeholder={
                                                    translations[
                                                        'enter_password'
                                                    ]
                                                }
                                                className={classNames(
                                                    'focus:ring-primary-light focus:border-primary-light block w-full',
                                                    errors.password?.type
                                                        ? 'border-red-300 bg-red-100'
                                                        : '',
                                                    'h-12'
                                                )}
                                                {...register('password', {
                                                    required: true,
                                                    minLength: 8,
                                                })}
                                            />
                                            <InputValidations
                                                field_error={
                                                    errors.password
                                                }
                                                required_key="common.login_password_required"
                                                minlength_key="common.login_valid_password_required"
                                            />
                                        </fieldset>
                                        <div className="flex gap-y-3 md:gap-y-0 items-start md:items-center  sm:flex-row justify-between keep-signin">
                                            <div className="flex justify-start gap-x-3 items-center checkbox">
                                                <input
                                                    type="checkbox"
                                                    className="border-gray-300 rounded-sm h-5 w-5"
                                                />
                                                <Translation content_key="keep_login" />
                                            </div>
                                            <div className="block  md:relative  ">
                                                <button
                                                    type="button"
                                                    className="forget-pwd"
                                                    onClick={() =>
                                                        setMode(
                                                            VIEW_MODES.FORGET_PASSWORD
                                                        )
                                                    }
                                                >
                                                    <Translation content_key="forgot_password" />
                                                </button>
                                            </div>
                                        </div>

                                        <button
                                            type="submit"
                                            className={classNames(
                                                'group relative w-full flex justify-center',
                                                ' border border-transparent',
                                                'text-md rounded-md text-white loginbtn',
                                                'focus:outline-none',
                                                loading
                                                    ? 'bg-primary-light'
                                                    : 'bg-primary hover:bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light',
                                                ' flex items-center'
                                            )}
                                            onClick={handleSubmit(onSubmit)}
                                        >
                                            <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                                                {loading ? (
                                                    <InputSpinner />
                                                ) : (
                                                    <LockClosedIcon
                                                        className="h-5 w-5 text-white opacity-0"
                                                        aria-hidden="true"
                                                    />
                                                )}
                                            </span>
                                            <Translation content_key="login_button" />
                                        </button>
                                    </>
                                ) : (
                                    ''
                                )}

                                {mode == VIEW_MODES.FORGET_PASSWORD ? (
                                    <button
                                        type="submit"
                                        className={classNames(
                                            'group relative w-full flex justify-center',
                                            'loginbtn border border-transparent',
                                            'text-md  text-white',
                                            'focus:outline-none',
                                            loading
                                                ? 'bg-primary-light'
                                                : 'bg-primary hover:bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light',
                                            ' flex items-center'
                                        )}
                                        onClick={handleSubmit(
                                            onSubmitRequestReset
                                        )}
                                    >
                                        <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                                            {loading ? (
                                                <InputSpinner />
                                            ) : (
                                                <LockClosedIcon
                                                    className="h-5 w-5 text-white opacity-0"
                                                    aria-hidden="true"
                                                />
                                            )}
                                        </span>
                                        <Translation content_key="send_reset_code" />
                                    </button>
                                ) : (
                                    ''
                                )}

                                {mode == VIEW_MODES.NEW_PASSWORD ? (
                                    <>
                                        <fieldset className="form-group">
                                            <label
                                                htmlFor="new_password"
                                                className={classNames(
                                                    'block ',
                                                    errors.new_password
                                                        ?.type
                                                        ? 'text-red-700'
                                                        : ''
                                                )}
                                            >
                                                <Translation content_key="common.login_new_password" />
                                            </label>
                                            <input
                                                type="password"
                                                id="new_password"
                                                autoComplete="new-password"
                                                className={classNames(
                                                    'focus:ring-primary-light focus:border-primary-light block w-full',
                                                    errors.new_password
                                                        ?.type
                                                        ? 'border-red-300 bg-red-100'
                                                        : ''
                                                )}
                                                {...register(
                                                    'new_password',
                                                    {
                                                        required: true,
                                                        minLength: 8,
                                                    }
                                                )}
                                            />
                                            <InputValidations
                                                field_error={
                                                    errors.new_password
                                                }
                                                required_key="common.login_password_required"
                                                minlength_key="common.login_valid_password_required"
                                            />
                                        </fieldset>

                                        <fieldset className="form-group">
                                            <label
                                                htmlFor="code"
                                                className={classNames(
                                                    'block ',
                                                    errors.code?.type
                                                        ? 'text-red-700'
                                                        : ''
                                                )}
                                            >
                                                <Translation content_key="common.login_code" />
                                            </label>

                                            <StyledMaskedTextBox
                                                className={classNames(
                                                    'focus:ring-primary-light focus:border-primary-light block w-full',
                                                    errors.code?.type
                                                        ? 'border-red-300 bg-red-100'
                                                        : ''
                                                )}
                                                id="code"
                                                mask="000000"
                                                {...register('code', {
                                                    required: true,
                                                })}
                                            />
                                            <InputValidations
                                                field_error={errors.code}
                                                required_key="common.login_code_required"
                                            />
                                        </fieldset>

                                        <button
                                            type="submit"
                                            className={classNames(
                                                'group relative w-full flex justify-center',
                                                'loginbtn border border-transparent',
                                                'text-md  text-white',
                                                'focus:outline-none',
                                                loading
                                                    ? 'bg-primary-light'
                                                    : 'bg-primary hover:bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light',
                                                ' flex items-center'
                                            )}
                                            onClick={handleSubmit(
                                                onSubmitNewPassword
                                            )}
                                        >
                                            <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                                                {loading ? (
                                                    <InputSpinner />
                                                ) : (
                                                    <LockClosedIcon
                                                        className="h-5 w-5 text-white opacity-0"
                                                        aria-hidden="true"
                                                    />
                                                )}
                                            </span>
                                            <Translation content_key="update_acct_password" />
                                        </button>
                                    </>
                                ) : (
                                    ''
                                )}

                                {mode == VIEW_MODES.REQUIRE_NEW_PASSWORD ? (
                                    <>
                                        <fieldset className="form-group">
                                            <label
                                                htmlFor="new_password"
                                                className={classNames(
                                                    'block ',
                                                    errors.new_password
                                                        ?.type
                                                        ? 'text-red-700'
                                                        : ''
                                                )}
                                            >
                                                <Translation content_key="new_password" />
                                            </label>
                                            <input
                                                type="password"
                                                id="new_password"
                                                autoComplete="new-password"
                                                className={classNames(
                                                    'focus:ring-primary-light focus:border-primary-light block w-full',
                                                    errors.new_password
                                                        ?.type
                                                        ? 'border-red-300 bg-red-100'
                                                        : ''
                                                )}
                                                {...register(
                                                    'new_password',
                                                    {
                                                        required: true,
                                                        minLength: 8,
                                                    }
                                                )}
                                            />
                                            <InputValidations
                                                field_error={
                                                    errors.new_password
                                                }
                                                required_key="common.login_password_required"
                                                minlength_key="common.login_valid_password_required"
                                            />
                                        </fieldset>

                                        <button
                                            type="submit"
                                            className={classNames(
                                                'group relative w-full flex justify-center',
                                                'loginbtn border border-transparent',
                                                'text-md  text-white',
                                                'focus:outline-none',
                                                loading
                                                    ? 'bg-primary-light'
                                                    : 'bg-primary hover:bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light',
                                                ' flex items-center'
                                            )}
                                            onClick={handleSubmit(
                                                onSubmitNewPasswordWithChallenge
                                            )}
                                        >
                                            <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                                                {loading ? (
                                                    <InputSpinner />
                                                ) : (
                                                    <LockClosedIcon
                                                        className="h-5 w-5 text-white opacity-0"
                                                        aria-hidden="true"
                                                    />
                                                )}
                                            </span>
                                            <Translation content_key="update_acct_password" />
                                        </button>
                                    </>
                                ) : (
                                    ''
                                )}
                            </div>

                            {Object.values(CognitoErrorTypes).includes(
                                errors.email?.type as CognitoErrorTypes
                            ) ? (
                                <div className="bg-red-100 rounded px-3 py-3 mb-5 mt-12 md:mt-5">
                                    {errors.email?.message && (
                                        <span className="text-sm text-red-700">
                                            <Translation
                                                content_key={
                                                    errors.email?.message
                                                }
                                            />
                                        </span>
                                    )}
                                </div>
                            ) : (
                                ''
                            )}

                            {mode == VIEW_MODES.LOGIN && resend_email ? (
                                <div className="z-20 flex justify-center md:justify-end">
                                    <button
                                        type="button"
                                        onClick={onResendEmail}
                                    >
                                        <Translation content_key="resend_email" />
                                    </button>
                                </div>
                            ) : (
                                <></>
                            )}
                        </form>
                    </div>
                    <div className="flex flex-col items-center">
                        <div className="dont-h-acunt">
                            <Translation content_key="dont_have_account" />
                        </div>
                        <div className="start-free">
                            <button
                                className="px-6 py-2 rounded-md text-lg  text-secondary-cyan"
                                style={{
                                    border: '1px solid #D8DADB',
                                }}
                                onClick={() => {
                                    router.push('/signup')
                                    // ctx.LoginModal.close()
                                }}
                            >
                                <Translation content_key="start_free_trial_now" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="md:w-1/2 sm:w-full loginimg">
                {/* <div
                    hidden={hide_close_button}
                    onClick={() => router.replace('/')}
                    className="absolute right-6 top-6"
                >
                    <LoginModalCloser className="self-center close-login" />
                </div> */}

                <div className="loginimg-info">
                    <figure>
                        <img src={`/images/aot-login-image.svg`} alt="" />
                    </figure>
                    {/* <div className="content-info">
                        <div className=" flex justify-center">
                            <p
                                style={primaryColorStyle}
                                className={labelStyles}
                            >
                                <Translation content_key="your" />
                            </p>
                            &nbsp;
                            <p
                                className={classNames(
                                    'text-secondary-cyan',
                                    labelStyles
                                )}
                            >
                                <Translation content_key="all_in_one" />
                            </p>
                            &nbsp;
                            <p
                                style={primaryColorStyle}
                                className={labelStyles}
                            >
                                <Translation content_key="scheduling" />
                            </p>
                            &nbsp;
                        </div>
                        <div className="">
                            <p
                                style={primaryColorStyle}
                                className={labelStyles}
                            >
                                <Translation content_key="booking_mgt" />
                            </p>
                        </div>
                    </div> */}
                </div>
            </div>
        </>
    )
}
